import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import axios from "axios";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";
import LoginPage from "@/pages/LoginPage";
import MagicLinkPage from "@/pages/MagicLinkPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import CommissionsPage from "@/pages/CommissionsPage";
import DashboardPage from "@/pages/DashboardPage";
import ProfilePage from "@/pages/ProfilePage";
import AdminPage from "@/pages/AdminPage";
import ApplicationAreasPage from "@/pages/ApplicationAreasPage";
import FindIndicationPage from "@/pages/FindIndicationPage";
import KnowUmifulPage from "@/pages/KnowUmifulPage";
import ScientificStudiesPage from "@/pages/ScientificStudiesPage";
import ProtocolsPage from "@/pages/ProtocolsPage";
import MaterialsPage from "@/pages/MaterialsPage";
import FeedbackPage from "@/pages/FeedbackPage";
import RaccontaCasoPage from "@/pages/RaccontaCasoPage";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNTRACKED_PATHS = ["/login", "/accedi", "/reset-password", "/register"];

/** Registra una pagina vista a ogni cambio rotta, solo per utenti autenticati. */
function PageViewTracker() {
  const location = useLocation();
  const { user } = useAuth();
  useEffect(() => {
    const path = location.pathname;
    if (!user?.token || UNTRACKED_PATHS.includes(path)) return;
    axios
      .post(`${API}/track/pageview`, { path }, { headers: { Authorization: `Bearer ${user.token}` } })
      .catch(() => {});
  }, [location.pathname, user?.token]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageViewTracker />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/accedi" element={<MagicLinkPage />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ProfilePage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <DashboardLayout>
                  <AdminPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/aree-applicazione"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ApplicationAreasPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/trova-indicazione"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <FindIndicationPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/conosci-umiful"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <KnowUmifulPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/approfondimenti-scientifici"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ScientificStudiesPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/protocolli"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ProtocolsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/racconta-un-caso"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <RaccontaCasoPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/le-mie-provvigioni"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CommissionsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/materiali-download"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MaterialsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/feedback-professionisti"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <FeedbackPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}

export default App;
